import { isArray } from '@apollo/client/cache/inmemory/helpers'
import { getCollection } from '@commerce/api/operations'
import { Error, Layout, Loader, SeoHead } from '@components/common'
import AlgoliaCollection from '@components/containers/AlgoliaCollection'
import SearchClient, {
  getDefaultSearchString,
  hasColourQuery,
  secureSearchClient,
} from '@lib/algolia'
import { collectionHandles } from '@lib/buildPaths'
import getGlobalStaticProps from '@lib/globalStaticProps'
import { getConfigByLocale } from '@lib/locale'
import cn from 'clsx'
import type {
  GetStaticPathsContext,
  GetStaticPropsContext,
  InferGetStaticPropsType,
} from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useRouter } from 'next/router'
import { createClient } from 'prismicio'
import * as R from 'ramda'
import { useEffect, useState } from 'react'
import { renderToString } from 'react-dom/server'
import { getServerState } from 'react-instantsearch'

type CollectionPageProps = {
  collection: InferGetStaticPropsType<typeof getStaticProps>
  serverState?: any
  algoliaIndex: string
  collectionHandle: string
  defaultfilterString: string
  collectionType?: any
  locale: string
  serverUrl: string
}

export async function getStaticProps({
  params,
  locale,
  previewData,
}: GetStaticPropsContext<{ handle: string[] }>) {
  const client = createClient({ previewData })
  const globalProps = await getGlobalStaticProps(client, locale)
  const translations = await serverSideTranslations(locale ?? 'en-au', [
    'common',
  ])
  const handles = params?.handle
  const collectionHandle = isArray(handles) ? handles.join('-') : handles
  const collection: any = await getCollection(
    locale,
    params?.handle.join('-') || '',
    'no-cache'
  )

  if (!collection) {
    return { notFound: true }
  }

  // render algolia collection on server
  const algoliaIndex = getConfigByLocale(locale, 'algoliaProductIndex')
  const defaultfilterString = getDefaultSearchString(
    `collections:${collectionHandle}`
  )
  const collectionType: any = R.find(
    R.propEq('key', 'collection_type'),
    collection?.metafields || []
  )
  const serverState = await getServerState(
    <AlgoliaCollection
      searchClient={secureSearchClient}
      indexName={algoliaIndex}
      defaultCategory={collectionHandle || ''}
      filterString={defaultfilterString}
      collectionType={collectionType?.value}
      locale={locale}
    />,
    { renderToString }
  )

  return {
    props: {
      ...globalProps,
      ...translations,
      collection,
      serverState,
      algoliaIndex,
      collectionHandle,
      defaultfilterString,
      collectionType: collectionType || null,
      locale,
    },
    // revalidate: 86400, // Refresh every 24 hours
  }
}

export async function getStaticPaths({ locales }: GetStaticPathsContext) {
  const paths = collectionHandles
    .map((h) => {
      return locales?.map((locale) => {
        return { params: { handle: [h] }, locale }
      })
    })
    .flat()

  return {
    paths,
    // Fallback shouldn't be enabled here or otherwise this route
    // will catch every page, even 404s, and we don't want that
    fallback: false,
  }
}

export default function Collection({
  collection,
  serverState,
  algoliaIndex,
  collectionHandle,
  defaultfilterString,
  collectionType,
  locale,
}: CollectionPageProps) {
  const { query, isReady } = useRouter()
  const [algoliaServerState, setAlgoliaServerState] = useState({})
  useEffect(() => {
    if (isReady) {
      hasColourQuery(query) &&
        window?.dispatchEvent(new CustomEvent('ColourFilterDetectedFromUrl'))
      !hasColourQuery(query) && setAlgoliaServerState(serverState)
    }
  }, [isReady])
  useEffect(() => {
    const handleServerState = () => {
      setAlgoliaServerState({})
    }
    window.addEventListener('ColourFilterDetectedFromUrl', handleServerState)
    return () => {
      window.removeEventListener(
        'ColourFilterDetectedFromUrl',
        handleServerState
      )
    }
  }, [])
  if (!isReady) return <Loader title="collection-loader" />
  if (!collection) {
    return <Error code={404} />
  }

  const { title, description, image, seo }: any = collection || {}
  const seoTitle = seo?.title || title
  const seoDescription = seo?.description || description

  return (
    <div
      className={
        collectionHandle === 'colour-lab' ? 'bg-white' : 'bg-ui-light-grey'
      }
    >
      <SeoHead title={seoTitle} description={seoDescription} image={image} />
      <div className="">
        <div
          className={cn('wrapper pt-16 lg:pt-30', {
            'pb-8 lg:pb-14':
              collectionType !== 'colour' && collectionType !== 'farbe',
          })}
        >
          <h1 className="text-mobileh1 text-ui-dark-grey lg:text-h1 ">
            {title}
          </h1>
          <p className="mt-4 text-mobileh4 text-ui-dark-grey lg:text-h4">
            {description}
          </p>
        </div>
        <AlgoliaCollection
          searchClient={SearchClient}
          indexName={algoliaIndex}
          defaultCategory={collectionHandle || ''}
          filterString={defaultfilterString}
          collectionType={collectionType?.value}
          serverState={algoliaServerState}
          locale={locale}
        />
      </div>
    </div>
  )
}

Collection.Layout = Layout
